import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import { useAuthStore } from '@/lib/store'
import {
  Search,
  ArrowLeft,
  DollarSign,
  User,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  GoalIcon as PaypalIcon,
  Banknote,
  Building2,
  Globe,
  TrendingUp,
  Bell,
  Plus,
  ChevronDown,
  Info
} from 'lucide-react'

const PAYMENT_TYPE_ICONS = {
  'paypal': PaypalIcon,
  'cashapp': DollarSign,
  'zelle': Banknote,
  'moncash': DollarSign,
  'western_union': Building2,
  'bank_transfer': Building2,
  'other': Globe
}

const PAYMENT_TYPE_LABELS = {
  'paypal': 'PayPal',
  'cashapp': 'Cash App',
  'zelle': 'Zelle',
  'moncash': 'MonCash',
  'western_union': 'Western Union',
  'bank_transfer': 'Bank Transfer',
  'other': 'Other'
}

interface SellerProfile {
  id: string;
  full_name: string | null;
  email: string | null;
  avatar_url: string | null;
}

interface SellerDetails {
  id: string;
  full_name: string | null;
  email: string | null;
  avatar_url: string | null;
  total_revenue: number;
  total_sales: number;
  payment_methods: any[];
  store_status: string;
}

export function AdminPaymentsPage() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [sellers, setSellers] = useState<SellerDetails[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [selectedSeller, setSelectedSeller] = useState<SellerDetails | null>(null)
  const [showSellerDetails, setShowSellerDetails] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [paymentAmount, setPaymentAmount] = useState('')
  const [paymentNotes, setPaymentNotes] = useState('')
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('')
  const [paymentHistory, setPaymentHistory] = useState<any[]>([])
  const [selectedMethodDetails, setSelectedMethodDetails] = useState<any>(null)

  useEffect(() => {
    if (user) {
      fetchSellers()
    }
  }, [user, searchQuery])

  const fetchSellers = async () => {
    try {
      setLoading(true)
      
      const { data: sellerStores, error: storeError } = await supabase
        .from('seller_stores')
        .select(`
          seller_id,
          status,
          total_sales,
          total_revenue,
          profiles!inner (
            id,
            full_name,
            email,
            avatar_url
          )
        `)
        .eq('status', 'approved')
        .order('created_at', { ascending: false })

      if (storeError) throw storeError

      // Then get payment methods for each seller
      const sellersWithMethods = await Promise.all(
        (sellerStores || []).map(async (store) => {
          const { data: methods, error: methodsError } = await supabase
            .from('payment_methods')
            .select('*')
            .eq('seller_id', store.seller_id)

          if (methodsError) throw methodsError

          const profiles = Array.isArray(store.profiles) ? store.profiles[0] : store.profiles
          const profile = profiles as SellerProfile

          return {
            id: store.seller_id,
            full_name: profile.full_name,
            email: profile.email,
            avatar_url: profile.avatar_url,
            payment_methods: methods || [],
            store_status: store.status,
            total_sales: store.total_sales || 0,
            total_revenue: store.total_revenue || 0
          }
        })
      )

      // Filter by search query if needed
      const filteredSellers = searchQuery
        ? sellersWithMethods.filter(seller => 
            seller.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            seller.email?.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : sellersWithMethods

      setSellers(filteredSellers)
    } catch (err) {
      console.error('Error fetching sellers:', err)
      showError('Failed to load sellers')
    } finally {
      setLoading(false)
    }
  }

  const fetchPaymentHistory = async (sellerId: string) => {
    try {
      const { data, error } = await supabase
        .from('payment_transactions')
        .select('*')
        .eq('seller_id', sellerId)
        .order('payment_date', { ascending: false })

      if (error) throw error
      setPaymentHistory(data || [])
    } catch (err) {
      console.error('Error fetching payment history:', err)
      showError('Failed to load payment history')
    }
  }

  const handleSellerClick = async (seller: SellerDetails) => {
    setSelectedSeller(seller)
    await fetchPaymentHistory(seller.id)
    setShowSellerDetails(true)
  }

  const handlePaymentMethodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const methodId = e.target.value
    setSelectedPaymentMethod(methodId)
    
    if (methodId && selectedSeller) {
      const method = selectedSeller.payment_methods.find(m => m.id === methodId)
      setSelectedMethodDetails(method)
    } else {
      setSelectedMethodDetails(null)
    }
  }

  const recordPayment = async () => {
    if (!selectedSeller || !selectedPaymentMethod || !paymentAmount || !user) {
      showError('Please fill in all required fields')
      return
    }

    try {
      const { error: transactionError } = await supabase
        .from('payment_transactions')
        .insert([{
          seller_id: selectedSeller.id,
          amount: parseFloat(paymentAmount),
          payment_method_id: selectedPaymentMethod,
          status: 'completed',
          notes: paymentNotes,
          created_by: user.id
        }])

      if (transactionError) throw transactionError

      await supabase
        .from('notifications')
        .insert([{
          user_id: selectedSeller.id,
          title: 'Paiement Reçu',
          content: `Un paiement de $${paymentAmount} a été effectué sur votre compte.`,
          type: 'system'
        }])

      showSuccess('Payment recorded successfully')
      setShowPaymentModal(false)
      setPaymentAmount('')
      setPaymentNotes('')
      setSelectedPaymentMethod('')
      setSelectedMethodDetails(null)
      await fetchPaymentHistory(selectedSeller.id)
    } catch (err) {
      console.error('Error recording payment:', err)
      showError('Failed to record payment')
    }
  }

  const showSuccess = (message: string) => {
    setToastMessage(message)
    setToastType('success')
    setShowToast(true)
  }

  const showError = (message: string) => {
    setToastMessage(message)
    setToastType('error')
    setShowToast(true)
  }

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="mr-4"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Gestion des Paiements
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Gérez les paiements des vendeurs approuvés
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-8">
              <div className="flex-1 max-w-lg">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher un vendeur..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : sellers.length === 0 ? (
              <div className="text-center py-12">
                <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun vendeur approuvé</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Aucun vendeur approuvé n'a encore été enregistré
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vendeur
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Revenus Totaux
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ventes
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Méthodes de Paiement
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sellers.map((seller) => (
                      <tr key={seller.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0">
                              {seller.avatar_url ? (
                                <img
                                  src={seller.avatar_url}
                                  alt={seller.full_name || ''}
                                  className="h-10 w-10 rounded-full"
                                />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                  <User className="h-5 w-5 text-gray-500" />
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {seller.full_name || 'Sans nom'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {seller.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900">
                            <DollarSign className="h-5 w-5 text-green-500 mr-1" />
                            {seller.total_revenue.toFixed(2)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900">
                            <TrendingUp className="h-5 w-5 text-blue-500 mr-1" />
                            {seller.total_sales}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex space-x-2">
                            {seller.payment_methods.map((method: any) => {
                              const Icon = PAYMENT_TYPE_ICONS[method.type as keyof typeof PAYMENT_TYPE_ICONS]
                              return (
                                <div
                                  key={method.id}
                                  className="flex items-center bg-gray-100 rounded-full px-3 py-1"
                                >
                                  <Icon className="h-4 w-4 text-gray-500 mr-1" />
                                  <span className="text-xs text-gray-700">
                                    {PAYMENT_TYPE_LABELS[method.type as keyof typeof PAYMENT_TYPE_LABELS]}
                                  </span>
                                </div>
                              )
                            })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSellerClick(seller)}
                          >
                            Détails
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Seller Details Modal */}
      {showSellerDetails && selectedSeller && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6">
              <div className="flex justify-between items-start">
                <h3 className="text-lg font-medium text-gray-900">
                  Détails du Vendeur
                </h3>
                <button
                  onClick={() => setShowSellerDetails(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <div className="mt-6 space-y-6">
                {/* Seller Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Informations</h4>
                  <div className="mt-2 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-900">{selectedSeller.full_name}</p>
                      <p className="text-sm text-gray-500">{selectedSeller.email}</p>
                    </div>
                  </div>
                </div>

                {/* Revenue Stats */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Statistiques</h4>
                  <dl className="mt-2 grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <dt className="text-sm font-medium text-gray-500">
                        Revenus Totaux
                      </dt>
                      <dd className="mt-1 text-2xl font-semibold text-gray-900">
                        ${selectedSeller.total_revenue.toFixed(2)}
                      </dd>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <dt className="text-sm font-medium text-gray-500">
                        Ventes Totales
                      </dt>
                      <dd className="mt-1 text-2xl font-semibold text-gray-900">
                        {selectedSeller.total_sales}
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* Payment Methods */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Méthodes de Paiement</h4>
                  <div className="mt-2 space-y-4">
                    {selectedSeller.payment_methods.map((method: any) => {
                      const Icon = PAYMENT_TYPE_ICONS[method.type as keyof typeof PAYMENT_TYPE_ICONS]
                      return (
                        <div key={method.id} className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <Icon className="h-5 w-5 text-gray-400 mr-2" />
                              <span className="text-sm font-medium text-gray-900">
                                {PAYMENT_TYPE_LABELS[method.type as keyof typeof PAYMENT_TYPE_LABELS]}
                              </span>
                            </div>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              method.is_enabled
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {method.is_enabled ? 'Actif' : 'Inactif'}
                            </span>
                          </div>
                          <div className="mt-2 text-sm text-gray-500">
                            {Object.entries(method.details).map(([key, value]) => (
                              <div key={key} className="capitalize">
                                {key}: {value as string}
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Payment History */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Historique des Paiements</h4>
                  <div className="mt-2 space-y-4">
                    {paymentHistory.length === 0 ? (
                      <div className="text-center py-6 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Aucun paiement enregistré</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Méthode</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {paymentHistory.map((payment: any) => (
                              <tr key={payment.id}>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                  {formatDate(payment.payment_date)}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {formatAmount(payment.amount)}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                  {payment.payment_method_type}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    payment.status === 'completed'
                                      ? 'bg-green-100 text-green-800'
                                      : payment.status === 'pending'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {payment.status === 'completed' && <CheckCircle className="h-3 w-3 mr-1" />}
                                    {payment.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                                    {payment.status === 'failed' && <XCircle className="h-3 w-3 mr-1" />}
                                    {payment.status === 'completed' ? 'Complété' :
                                     payment.status === 'pending' ? 'En attente' : 'Échoué'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                  {payment.notes}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="pt-6 border-t flex justify-end space-x-3">
                  <Button
                    variant="primary"
                    onClick={() => {
                      setShowPaymentModal(true)
                      setShowSellerDetails(false)
                    }}
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Enregistrer un Paiement
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedSeller && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-md w-full m-4">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Enregistrer un Paiement
                </h3>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Montant
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <DollarSign className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Méthode de Paiement
                  </label>
                  <select
                    value={selectedPaymentMethod}
                    onChange={handlePaymentMethodChange}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  >
                    <option value="">Sélectionner une méthode</option>
                    {selectedSeller.payment_methods.map((method: any) => (
                      <option key={method.id} value={method.id}>
                        {PAYMENT_TYPE_LABELS[method.type as keyof typeof PAYMENT_TYPE_LABELS]}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedMethodDetails && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <Info className="h-5 w-5 text-blue-400 mr-2" />
                      <h4 className="text-sm font-medium text-blue-900">
                        Détails de la méthode de paiement
                      </h4>
                    </div>
                    <div className="mt-2 space-y-2">
                      {Object.entries(selectedMethodDetails.details).map(([key, value]) => (
                        <div key={key} className="flex items-center text-sm text-blue-800">
                          <span className="font-medium capitalize">{key}:</span>
                          <span className="ml-2">{value as string}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Notes
                  </label>
                  <textarea
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    placeholder="Ajouter des notes sur le paiement..."
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowPaymentModal(false)}
                >
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  onClick={recordPayment}
                  disabled={!paymentAmount || !selectedPaymentMethod}
                >
                  Enregistrer le Paiement
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}